import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../context/LocaleContext';
import { useProducts } from '../../context/ProductsContext';
import { useMedia } from '../../context/MediaContext';
import { Product } from '../../types/product-variant';
import { pickProductMediaUrl, pickVariantImageUrl } from '../../utils/media';
import { cloudinaryUrl } from '../../utils/cloudinary';

const LatestProducts: React.FC = () => {
    const { t } = useTranslation();
    const { localePath } = useLocale();
    const { products } = useProducts();
    const { getMediaByProduct, loading: mediaLoading } = useMedia();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showProducts, setShowProducts] = useState(false);
    const typedProducts = products as Product[];

    // --- Move helpers up so they're available for useMemo ---
    const resolveProductMedia = (product: Product) => {
        if (mediaLoading) return [];
        let media = product.airtableId ? getMediaByProduct(product.airtableId) : [];
        if (media.length === 0 && product.id) {
            media = getMediaByProduct(product.id);
        }
        if (media.length === 0 && product.slug) {
            media = getMediaByProduct(product.slug);
        }
        return media;
    };

    const pickMediaUrl = (product: Product, usage: string, fallbackHints: string[]) => {
        const media = resolveProductMedia(product);
        return pickProductMediaUrl(media, fallbackHints, usage);
    };

    const logMediaPick = (product: Product) => {
        if (!import.meta.env.DEV) return;
        const media = resolveProductMedia(product);
        const pickedMain = pickProductMediaUrl(media, ['-main', ' main', 'main-', '_main', '-primary', 'primary', 'front', 'principal'], 'product-main');
        const pickedSecondary = pickProductMediaUrl(media, ['-secondary', 'secondary', '-model', 'model', '-modelo', 'modelo'], 'product-secondary');
        const pickedThird = pickProductMediaUrl(media, ['-third', 'third', '-zoom', 'zoom'], 'product-third');
        console.debug('[media] featured card', {
            id: product.id,
            name: product.name,
            airtableId: product.airtableId,
            mediaCount: media.length,
            mediaIds: media.map(m => m.id),
            picked: { main: pickedMain, secondary: pickedSecondary, third: pickedThird },
        });
    };

    // Add resize listener
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Use useMemo to prevent recalculating on every render
    const randomProducts = useMemo(() => {
        // Create a copy of the products array to avoid mutating the original
        const shuffled = [...typedProducts]
            // Fisher-Yates shuffle algorithm
            .sort(() => Math.random() - 0.5)
            // Take first 4 items on mobile, 8 on desktop
            .slice(0, isMobile ? 4 : 8);

        // Sort the selected products to ensure a mix of collections
        return shuffled.sort((a, b) => {
            // First sort by collection
            const collectionA = a.collection?.toLowerCase() || '';
            const collectionB = b.collection?.toLowerCase() || '';
            if (collectionA !== collectionB) {
                return collectionA < collectionB ? -1 : 1;
            }
            
            // Then by category within each collection
            const categoryA = a.category?.toLowerCase() || '';
            const categoryB = b.category?.toLowerCase() || '';
            if (categoryA !== categoryB) {
                return categoryA < categoryB ? -1 : 1;
            }
            
            return 0;
        });
    }, [typedProducts, isMobile]); // Add isMobile to dependencies

    // Sort products so those with images load first
    const sortedProducts = useMemo(() => {
        return randomProducts.slice().sort((a, b) => {
            const aHasImage = !!pickMediaUrl(a, 'product-main', ['-main', ' main', 'main-', '_main', '-primary', 'primary', 'front', 'principal']);
            const bHasImage = !!pickMediaUrl(b, 'product-main', ['-main', ' main', 'main-', '_main', '-primary', 'primary', 'front', 'principal']);
            if (aHasImage === bHasImage) return 0;
            return aHasImage ? -1 : 1;
        });
    }, [randomProducts]);

    useEffect(() => {
        const timer = setTimeout(() => setShowProducts(true), 150); // 150ms delay (was 350ms)
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="latest-products -mt-10 md:py-8 mb-20">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-8 text-dark dark:text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]">
                {t('product.featuredProducts')}
            </h2>
            <div className="relative w-screen left-1/2 -ml-[50vw] md:w-auto md:left-auto md:ml-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px md:gap-8">
                <AnimatePresence>
                {showProducts && !mediaLoading && sortedProducts.map(product => (
                    <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 30 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        whileHover={{ scale: 1.05 }}
                        className="bg-white dark:bg-brand-charcoal rounded-none md:rounded-xl shadow-none md:shadow-md overflow-hidden"
                    >
                        {import.meta.env.DEV ? (logMediaPick(product), null) : null}
                        <Link to={localePath('/product/:slug', { slug: product.slug })}>
                            <div className="relative">
                                <img
                                    src={cloudinaryUrl(
                                        pickMediaUrl(product, 'product-main', ['-main', ' main', 'main-', '_main', '-primary', 'primary', 'front', 'principal']) ||
                                        pickVariantImageUrl(product.variants?.[0]?.images, 0) ||
                                        pickMediaUrl(product, 'product-secondary', ['-secondary', 'secondary', '-model', 'model', '-modelo', 'modelo']) ||
                                        pickVariantImageUrl(product.variants?.[0]?.images, 1) ||
                                        pickMediaUrl(product, 'product-third', ['-third', 'third', '-zoom', 'zoom']) ||
                                        pickVariantImageUrl(product.variants?.[0]?.images, 2) ||
                                        '',
                                        400
                                    )}
                                    alt={product.name}
                                    className="w-full aspect-square md:aspect-auto md:h-64 object-cover"
                                />
                                {product.oneOfKind && (
                                    <div className="absolute top-2 right-2 bg-brand-blue/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                                        <FontAwesomeIcon icon={faStar} className="h-3 w-3" />
                                        <span>{t('product.oneOfKindBadge')}</span>
                                    </div>
                                )}
                                {product.variants && product.variants.length > 0 && (
                                    <div className="absolute top-2 left-2 md:top-auto md:bottom-2 md:left-auto md:right-2 bg-brand-blue/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                                        {t('product.multipleOptions')}
                                    </div>
                                )}
                                {/* Mobile overlay text */}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2 md:hidden">
                                    <h3 className="text-sm font-medium text-white truncate">{product.name}</h3>
                                    <p className="text-xs text-white/90">
                                        {product.variants && product.variants.length > 0
                                            ? `${t('product.from')} €${(product.variants.reduce((min, v) => Math.min(min, v.price || Infinity), Infinity) !== Infinity ? product.variants.reduce((min, v) => Math.min(min, v.price || Infinity), Infinity) : product.price)?.toFixed(2)}`
                                            : `€${product.price?.toFixed(2)}`
                                        }
                                    </p>
                                </div>
                            </div>
                            {/* Desktop text area */}
                            <div className="hidden md:block p-4">
                                <h3 className="text-xl font-semibold text-brand-dark dark:text-white mb-2">
                                    {product.name}
                                </h3>
                                <p className="text-brand-dark dark:text-white">
                                    {product.variants && product.variants.length > 0
                                        ? `${t('product.from')} €${(product.variants.reduce((min, v) => Math.min(min, v.price || Infinity), Infinity) !== Infinity ? product.variants.reduce((min, v) => Math.min(min, v.price || Infinity), Infinity) : product.price)?.toFixed(2)}`
                                        : `€${product.price?.toFixed(2)}`
                                    }
                                </p>
                            </div>
                        </Link>
                    </motion.div>
                ))}
                </AnimatePresence>
            </div>
            </div>
        </div>
    );
}

export default LatestProducts;